import i18next from 'i18next';
import { ReactElement, ReactNode, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useRecoilValue } from 'recoil';
import { userInfoState } from 'recoil/user/atom';
import { ROLE_TYPE } from 'types/enumType';
import { decryptId, decryptString } from 'util/encryptUtil';
import { getDiffRole } from '../util/commonUtil';

interface AuthGuardProps {
  children: ReactNode;
}

const getNavigateAfterCheckLogin = (
  url: string,
  isAuthenticated: boolean,
): ReactElement | null => {
  if (isAuthenticated) {
    if (url === '/sign-in' || url === '/sign-up' || url === '/') {
      return <Navigate to={'/home/status-board'} replace />;
    }
  } else {
    if (!(url === '/sign-in' || url === '/sign-up')) {
      return <Navigate to={'/sign-in'} />;
    }

    return null;
  }

  return null;
};

const getNavigateAfterCheckAuthority = (
  url: string,
  role: string,
  isSuper: boolean,
): ReactElement | null => {
  if (url.startsWith('/setting') && !isSuper) {
    alert(i18next.t('validation.noAccess'));
    return <Navigate to={'/home/status-board'} replace />;
  }

  if (url.startsWith('/admin') && role !== ROLE_TYPE.ADMIN) {
    alert(i18next.t('validation.noAccess'));
    return <Navigate to={'/home/status-board'} replace />;
  }

  if (
    (url.includes('/self-permission') ||
      url.includes('/permission-transfer') ||
      url.includes('/box')) &&
    role === ROLE_TYPE.USER
  ) {
    alert(i18next.t('validation.noAccess'));
    return <Navigate to={'/home/status-board'} replace />;
  }

  return null;
};

const AuthGuard = ({ children }: AuthGuardProps) => {
  const location = useLocation();
  const url = location.pathname;

  const userInfo = useRecoilValue(userInfoState);
  const role = decryptString(userInfo.role);
  if (!role) {
    alert(
      `${i18next.t('validation.noAccess')}\n${i18next.t(
        'validation.retryLogin',
      )}`,
    );
    sessionStorage.clear();
    return <Navigate to={'/sign-in'} />;
  }

  const isSuperRole =
    role === ROLE_TYPE.ADMIN && !decryptId(userInfo.factoryId ?? '');

  const acceptRole = decryptString(userInfo.acceptUserRole);
  const currentRole = getDiffRole(role, acceptRole);

  const navigateElement =
    getNavigateAfterCheckLogin(url, userInfo.isAuthenticated) ||
    getNavigateAfterCheckAuthority(url, currentRole, isSuperRole);
  if (navigateElement) {
    return navigateElement;
  }

  return <Suspense>{children}</Suspense>;
};

export default AuthGuard;
